/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  Fragment,
  useCallback,
  useLayoutEffect,
  useState,
  useRef,
  useEffect,
} from 'react'
import cn from 'classnames'
import { t } from '@sportninja/common/i18n'

import LoadingSpinner from '../LoadingSpinner'
import { Pager } from '../Pagination'
import { SortIcon } from './css'
import { zIndex } from '../css'

const capitalize = (str) => {
  if (typeof str !== 'string' || str.length === 0) return str

  return str
    .split(' ')
    .map((word) => {
      return `${word.charAt(0).toUpperCase()}${word.slice(1)}`
    })
    .join(' ')
}

const StatisticsTable = ({
  columns,
  groupByType,
  hasSeasonColumn,
  isLoading,
  isMobile,
  isPaginated,
  rows,
  table,
  totalWidth,
}) => {
  const {
    headerGroups,
    prepareRow,
    pageCount,
    gotoPage,
    // Get the state from the instance
    state: { pageIndex },
  } = table

  const [visibleTableWidth, setVisibleTableWidth] = useState(
    window.innerWidth - 88 - 88
  )
  const tableRef = useRef(null)
  const headerRef = useRef(null)
  const mounted = useRef(null)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useLayoutEffect(() => {
    setTimeout(() => {
      const ref = tableRef.current
      if (ref) {
        const width = tableRef.current.getBoundingClientRect().width
        mounted.current && setVisibleTableWidth(width)
      }
    })
  }, [columns])

  const onScroll = useCallback(
    (e) => {
      const left = e.target.scrollLeft
      headerRef.current.scrollTo(left, 0)
      const availableScroll = totalWidth - visibleTableWidth

      if (left >= availableScroll) {
        e.target.classList.remove('show-right-fade')
        e.target.classList.add('show-left-fade')
      } else if (left === 0) {
        e.target.classList.remove('show-left-fade')
        e.target.classList.add('show-right-fade')
      } else if (left !== 0 || left < availableScroll) {
        e.target.classList.add('show-right-fade')
        e.target.classList.add('show-left-fade')
      }
    },
    [visibleTableWidth]
  )

  return (
    <Fragment>
      <div
        className='table stats-table'
        ref={tableRef}
        css={css`
          margin-bottom: ${isPaginated ? '40' : '0'}px;
        `}
      >
        {headerGroups.map((headerGroup) => {
          const props = headerGroup.getHeaderGroupProps() || {}
          return (
            <div className='header-group' key={props.key}>
              <div ref={headerRef} className='header-inner'>
                {headerGroup.headers.map((column) => {
                  if (column.isExpanded) return null
                  const { onClick, style, ...colProps } = column.getHeaderProps(
                    column.getSortByToggleProps()
                  )

                  // Monkey-patch sort click so that it sends you back to the first
                  // page.
                  const onColClick = (...args) => {
                    if (isPaginated) gotoPage(0)
                    onClick(...args)
                  }

                  const title = capitalize(column.fullName)

                  return (
                    // eslint-disable-next-line react/jsx-key
                    <div
                      onClick={onColClick}
                      {...colProps}
                      style={style}
                      className={cn('header', {
                        'is-name-col': ['name', 'season'].includes(column.id),
                        'is-team-name': column.id === 'Team',
                      })}
                      title={title}
                    >
                      {column.render('Header')}
                      {column.isSorted && (
                        <SortIcon
                          name={column.isSortedDesc ? 'sort-down' : 'sort-up'}
                        />
                      )}
                      {column.toolTip && (
                        <div className='header-tooltip'>{column.toolTip}</div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}

        <div
          className={cn('body', {
            'show-right-fade': !isLoading && visibleTableWidth < totalWidth,
            'has-season-column': hasSeasonColumn,
          })}
          onScroll={onScroll}
        >
          {isLoading ? (
            <div
              css={css`
                padding: 20px 0;
              `}
            >
              <LoadingSpinner />
            </div>
          ) : rows.length > 0 ? (
            rows.map((row, rowIdx) => {
              prepareRow(row)
              return row.isGrouped ? (
                <div
                  {...row.getToggleRowExpandedProps()}
                  {...row.getRowProps()}
                  className='is-grouped'
                >
                  <div className='grouped-cell'>
                    {row.cells[0].render('Cell')}
                  </div>
                </div>
              ) : (
                // eslint-disable-next-line react/jsx-key
                <div className='row' {...row.getRowProps()}>
                  {row.cells.map((cell, idx) => {
                    // Don't render the "grouped by" column - we'll display it
                    // in its row instead.
                    if (!row.isExpanded && cell.column.id === 'division')
                      return null

                    const props = cell.getCellProps() || {}

                    return (
                      <div
                        className={cn('cell', {
                          'is-name-col': ['name', 'season'].includes(
                            cell.column.id
                          ),
                          'is-sorted': cell.column.isSorted,
                          'is-row-1': rowIdx === 0,
                          'is-team-name': cell.column.id === 'Team',
                        })}
                        key={props.key}
                        style={{ minWidth: props.style.width }}
                      >
                        {cell.render('Cell', {
                          groupByType,
                          isMobile,
                          rowNumber:
                            cell?.row?.original?.player?.player_number ||
                            pageIndex * 25 + rowIdx + 1,
                        })}
                      </div>
                    )
                  })}
                </div>
              )
            })
          ) : (
            <div
              css={css`
                padding: 20px 0;
                text-align: center;
              `}
            >
              {t('Web:thereAreNoStatisticsAvailable')}
            </div>
          )}
        </div>
      </div>
      {isPaginated && (
        // Reset stacking context with another position: relative container, so
        // pagination appears above the "fades" in the table
        <div
          css={css`
            position: relative;

            .sn-pagination {
              z-index: ${zIndex.modal};
            }
          `}
        >
          <Pager
            page={pageIndex + 1}
            total={pageCount}
            onClick={(idx) => gotoPage(idx - 1)}
          />
        </div>
      )}
    </Fragment>
  )
}

export default StatisticsTable
